import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputBase,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Menu,
} from "@mui/material";
import {
  SearchOutlined as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  FilterListOutlined,
} from "@mui/icons-material";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import Button from "../../components/common/Button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import apiService from "../../service/Api";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import AddUserForm from "./AddUserForm";
import EditUserDialog from "./EditUserDialog";
import DataGrid from "../../components/common/CustomeDataGrid";

// Interface for API response
interface ApiUser {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  user_type: string;
  user_timezone: string;
  country: string | null;
  phone_number: string | null;
  role: string;
  organization: string | null;
  is_verified: boolean;
  profile_picture: string | null;
  individual: string | null;
  company: {
    id: number;
    name: string;
    description: string;
    phone_number: string | null;
    primary_email: string | null;
    secondary_email: string | null;
    website: string | null;
    time_zone: string;
    street_address: string;
    billing_address: string | null;
    city: string;
    company_type: string | null;
    state: string | null;
    postal_code: string | null;
    country: string;
    license_number: string | null;
    specialties: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    company_logo: string | null;
    latitude: string | null;
    longitude: string | null;
    created_by: string | null;
    last_updated_by: string | null;
  } | null;
  groups: string[];
  is_active: boolean;
  is_2fa_enabled: boolean;
  last_login: string | null;
}

// Interface for component state
interface User {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  name: string;
  phone: string | null;
  department: string | null;
  roles: string[];
  status: string;
  is_active: boolean;
  is_2fa_enabled: boolean;
  last_login: string | null;
  avatar: string;
  groups: string[];
}

export default function UserManagement() {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const apiPage = paginationModel.page + 1;
      const params: any = {
        page: apiPage,
        page_size: paginationModel.pageSize,
      };
      // Add search parameter if at least 3 characters
      if (appliedSearchTerm.length >= 3) {
        params.search = appliedSearchTerm;
      }
      // Add role filter (groups) if not "All Roles"
      if (roleFilter !== "All Roles") {
        params.groups__name = roleFilter;
      }
      // Add status filter (is_active) if not "All Status"
      if (statusFilter !== "All Status") {
        params.is_active = statusFilter === "Active" ? "true" : "false";
      }
      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: ApiUser[];
      }>("/api/authentication/users/", params);
      const mappedUsers: User[] = response.data.results.map((user) => ({
        id: user.id,
        email: user.email,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        name: `${user.first_name} ${user.last_name}`.trim() || user.username,
        phone: user.phone_number || null,
        department: user.company?.name || user.organization || null,
        roles: user.groups || [],
        status: user.is_active ? "Active" : "Suspended",
        is_active: user.is_active,
        is_2fa_enabled: user.is_2fa_enabled,
        last_login: user.last_login || null,
        avatar: user.first_name?.[0]?.toUpperCase() || user.username[0].toUpperCase(),
        groups: user.groups || [],
      }));
      setUsers(mappedUsers);
      setRowCount(response.data.count);
      setError(null);
    } catch (err: any) {
      let errorMessage = "Failed to load users. Please try again.";
      if (err.response?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        navigate("/login");
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Fetch users error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [paginationModel, appliedSearchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (inputValue.length >= 3 || inputValue === "") {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(inputValue);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }, 1000);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setAppliedSearchTerm(inputValue);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    userId: string
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  const handleRowClick = (params: { row: User }) => {
    navigate(`/user-management/view/${params.row.id}`);
  };

  const handleAddUserClick = () => {
    setUserFormOpen(true);
  };

  const handleUserFormClose = () => {
    setUserFormOpen(false);
  };

  const handleEditUser = () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      setSelectedUser(user);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleChangeRole = () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      setSelectedUser(user);
      setNewGroup(user.groups);
      setChangeRoleDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleSuspendUser = () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      setSelectedUser(user);
      setSuspendDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteUser = () => {
    const user = users.find((u) => u.id === selectedUserId);
    if (user) {
      setSelectedUser(user);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleSaveUser = async (userData: any) => {
    try {
      await apiService.post("/api/authentication/users/", userData);
      await fetchUsers();
      setUserFormOpen(false);
      toast.success("User created successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      setError("Failed to create user. Please try again.");
      toast.error("Failed to create user", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error(err);
    }
  };

  const handleSaveEditUser = async (userData: any) => {
    if (!selectedUser) return;
    try {
      const backendData = {
        ...userData,
        groups: userData.roles,
      };
      await apiService.patch(`/api/authentication/users/${selectedUser.id}/`, backendData);
      await fetchUsers();
      setEditDialogOpen(false);
      setSelectedUser(null);
      toast.success("User updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      setError("Failed to update user. Please try again.");
      toast.error("Failed to update user", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error(err);
    }
  };

  const handleSaveRoleChange = async () => {
    if (!selectedUser) return;
    try {
      await apiService.patch(`/api/authentication/users/${selectedUser.id}/`, {
        groups: newGroup,
      });
      await fetchUsers();
      setChangeRoleDialogOpen(false);
      setSelectedUser(null);
      setNewGroup([]);
      toast.success("User role updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      setError("Failed to update user role. Please try again.");
      toast.error("Failed to update user role", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error(err);
    }
  };

  const handleSaveSuspend = async () => {
    if (!selectedUser) return;
    try {
      await apiService.patch(`/api/authentication/users/${selectedUser.id}/`, {
        is_active: false,
      });
      await fetchUsers();
      setSuspendDialogOpen(false);
      setSelectedUser(null);
      toast.success("User suspended successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      setError("Failed to suspend user. Please try again.");
      toast.error("Failed to suspend user", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error(err);
    }
  };

  const handleSaveDelete = async () => {
    if (!selectedUser) return;
    try {
      await apiService.delete(`/api/authentication/users/${selectedUser.id}/`);
      await fetchUsers();
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      toast.success("User deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      setError("Failed to delete user. Please try again.");
      toast.error("Failed to delete user", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error(err);
    }
  };

  const getStatusColor = (status: string) => {
    return status === "Active" ? "success" : "warning";
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "Admin":
        return "error";
      case "Operations":
        return "secondary";
      case "Sales":
      case "Dev":
      case "Finance":
      case "Support":
        return "info";
      default:
        return "default";
    }
  };

  const columns: GridColDef<User>[] = [
    {
      field: "full_name",
      headerName: "Full Name",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: "0.875rem" }}>
            {params.row.avatar}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.email}
        </Typography>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.phone || "N/A"}
        </Typography>
      ),
    },
    {
      field: "roles",
      headerName: "Roles",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {(params.row.roles || []).map((role) => (
            <Chip
              key={role}
              label={role}
              size="small"
              color={getRoleColor(role) as any}
              variant="outlined"
            />
          ))}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: params.row.status === "Active" ? "#4caf50" : "#ff9800",
            }}
          />
          <Typography variant="body2">{params.row.status}</Typography>
        </Box>
      ),
    },
    {
      field: "last_login",
      headerName: "Last Login",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.last_login || "N/A"}
        </Typography>
      ),
    },
    {
      field: "is_2fa_enabled",
      headerName: "2FA",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.row.is_2fa_enabled ? "Enabled" : "Disabled"}
          size="small"
          color={params.row.is_2fa_enabled ? "success" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 150,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => handleMenuClick(e, params.row.id)}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button
              onClick={() => {
                setError(null);
                setPaginationModel({ page: 0, pageSize: 20 });
                fetchUsers();
              }}
              sx={{ ml: 2 }}
            >
              Retry
            </Button>
          </Alert>
        )}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  backgroundColor: reduxTheme.neutral_colors?.[900] || "#fff",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  pl: 1,
                  width: "30%",
                  border: `1px solid ${reduxTheme.neutral_colors?.[300] || "#ddd"}`,
                }}
              >
                <SearchIcon
                  sx={{
                    mr: 1,
                    color: reduxTheme.neutral_colors?.[100] || "#666",
                  }}
                />
                <InputBase
                  placeholder="Search by username, email or name..."
                  sx={{
                    ml: 1,
                    flex: 1,
                    color: reduxTheme.neutral_colors?.[100] || "#333",
                  }}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
                <IconButton
                  sx={{
                    color: reduxTheme.neutral_colors?.[100] || "#666",
                    "&:hover": {
                      backgroundColor: reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                    },
                  }}
                >
                  <FilterListOutlined />
                </IconButton>
              </Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>All Roles</InputLabel>
                <Select
                  value={roleFilter}
                  label="All Roles"
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="All Roles">All Roles</MenuItem>
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="Dev">Dev</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Support">Support</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>All Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="All Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="All Status">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineOutlined />}
              sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
              onClick={handleAddUserClick}
            >
              Add User
            </Button>
          </Box>
        </Box>
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            {(inputValue || roleFilter !== "All Roles" || statusFilter !== "All Status") && (
              <Button
                size="small"
                onClick={() => {
                  setInputValue("");
                  setAppliedSearchTerm("");
                  setRoleFilter("All Roles");
                  setStatusFilter("All Status");
                  setPaginationModel({ page: 0, pageSize: 10 });
                }}
                sx={{ textTransform: "none" }}
              >
                Clear all filters
              </Button>
            )}
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress
                sx={{
                  color: reduxTheme.primary_colors?.[400] || "#614438",
                }}
              />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or filters
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={users}
              columns={columns}
              rowCount={rowCount}
              loading={loading}
              paginationMode="server"
              paginationModel={paginationModel}
              onRowClick={handleRowClick}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: reduxTheme.primary_colors?.[500] || "#614438",
                  color: reduxTheme.primary_colors?.[100] || "#fff",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: reduxTheme.primary_colors?.[500] || "#614438",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: reduxTheme.primary_colors?.[100] || "#fff",
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "#fff",
                  justifyContent: "center",
                  padding: 1,
                },
              }}
            />
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleEditUser}>
              <EditIcon sx={{ mr: 1 }} />
              Edit User
            </MenuItem>
            <MenuItem onClick={handleChangeRole}>
              <GroupIcon sx={{ mr: 1 }} />
              Change Role
            </MenuItem>
            <MenuItem onClick={handleSuspendUser}>
              <BlockIcon sx={{ mr: 1 }} />
              Suspend User
            </MenuItem>
            <MenuItem onClick={handleDeleteUser}>
              <DeleteIcon sx={{ mr: 1 }} />
              Delete User
            </MenuItem>
          </Menu>
        </Paper>

        {/* Add User Form Dialog */}
        <AddUserForm
          open={userFormOpen}
          onClose={handleUserFormClose}
          onSave={handleSaveUser}
        />

        {/* Edit User Dialog */}
        <EditUserDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          onSave={handleSaveEditUser}
        />

        {/* Change Role Dialog */}
        <Dialog
          open={changeRoleDialogOpen}
          onClose={() => setChangeRoleDialogOpen(false)}
        >
          <DialogTitle>Change Role for {selectedUser?.username}</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Groups</InputLabel>
              <Select
                multiple
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value as string[])}
                label="Groups"
              >
                {["Admin", "Sales", "Dev", "Operations", "Finance", "Support"].map(
                  (group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangeRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRoleChange} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Suspend User Dialog */}
        <Dialog
          open={suspendDialogOpen}
          onClose={() => setSuspendDialogOpen(false)}
        >
          <DialogTitle>Suspend {selectedUser?.username}</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to suspend {selectedUser?.username}? This will
              deactivate their account.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSuspend} variant="contained" color="error">
              Suspend
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete {selectedUser?.username}</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {selectedUser?.username}? This
              action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
}